import { Button } from "@/components/ui/button";
import {
  Action,
  ControlType,
} from "@/context/AnalyticsProvider/AnalyticsProvider";
import { useHomepageAnalytics } from "@/context/AnalyticsProvider/homepage";
import { Gem } from "lucide-react";
import { useRef, useState } from "react";
import { useQuestCompletions } from "../QuestsV2/useQuestCompletions";
import { QuestCard } from "./QuestCard";
import { useQuestFilter } from "./QuestFilterProvider";

const INITIAL_DISPLAY_COUNT = 4;

export function QuestFilterResults() {
  const { filteredQuests, gameGroups } = useQuestFilter();
  const { completedQuests, mutate: invalidateQuestCompletions } =
    useQuestCompletions();
  const { track } = useHomepageAnalytics();

  const [showAll, setShowAll] = useState(false);
  const viewMoreButtonRef = useRef<HTMLDivElement>(null);

  if (filteredQuests.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center space-y-4">
        <div className="w-20 h-20 rounded-full bg-white/5 flex items-center justify-center">
          <Gem className="w-10 h-10 text-white/80" />
        </div>
        <div className="space-y-2">
          <h3 className="text-xl font-bold text-white">No quests available</h3>
          <p className="text-white/70 max-w-md">
            Check back soon for new quests or try adjusting your filters
          </p>
        </div>
      </div>
    );
  }

  const displayedGames = showAll
    ? gameGroups
    : gameGroups.slice(0, INITIAL_DISPLAY_COUNT);
  const hasMore = gameGroups.length > INITIAL_DISPLAY_COUNT;

  const handleShowLess = () => {
    setShowAll(false);
    track({
      action: Action.PRESSED,
      extras: {
        id: "quest-finder",
        control: "ViewLessGames",
        controlType: ControlType.BUTTON,
      },
    });
    // Scroll to where the "View More" button was positioned (bottom of viewport)
    setTimeout(() => {
      viewMoreButtonRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }, 100);
  };

  const handleViewMore = () => {
    setShowAll(true);
    track({
      action: Action.PRESSED,
      extras: {
        id: "quest-finder",
        control: "ViewMoreGames",
        controlType: ControlType.BUTTON,
      },
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-lg md:text-2xl font-bold text-white">
          {gameGroups.length} {gameGroups.length === 1 ? "Game" : "Games"} Found
        </h2>
        <p className="text-xs md:text-sm text-white/70 whitespace-nowrap">
          {filteredQuests.length} total quests
        </p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {displayedGames.map((gameGroup, index) => {
          const completedQuestsForGame = Object.values(
            completedQuests ?? {},
          ).filter((quest) => quest.game_id === gameGroup.gameId);
          return (
            <QuestCard
              completedQuests={completedQuestsForGame}
              invalidateQuestCompletions={invalidateQuestCompletions}
              key={gameGroup.gameId}
              gameName={gameGroup.gameName}
              gameLandingPageUrl={gameGroup.gameLandingPageUrl}
              gameIcon={gameGroup.gameIcon}
              gameSlug={gameGroup.gameSlug}
              gameGenres={gameGroup.gameGenres}
              featured={gameGroup.featured}
              gameId={gameGroup.gameId}
              quests={gameGroup.quests}
              displayOrder={index + 1}
            />
          );
        })}
      </div>

      {hasMore && !showAll && (
        <div ref={viewMoreButtonRef} className="flex justify-center pt-4">
          <Button
            variant="outline"
            size="lg"
            onClick={handleViewMore}
            className="min-w-[200px]"
          >
            View More Games ({gameGroups.length - INITIAL_DISPLAY_COUNT} more)
          </Button>
        </div>
      )}

      {hasMore && showAll && (
        <div className="flex justify-center pt-4">
          <Button
            variant="outline"
            size="lg"
            onClick={handleShowLess}
            className="min-w-[200px]"
          >
            Show Less
          </Button>
        </div>
      )}
    </div>
  );
}
